import * as XLSX from 'xlsx';
import type { TimeEntry, Project, Location, EntryProject } from '../types';
import { parseTime, formatTime } from './timeCalculator';

interface ParsedBlock {
  date: string;
  projectName: string;
  timeArrived: string;
  timeLeft: string;
  breakMinutes: number;
  description: string;
  locationName: string;
}

const parseDate = (val: unknown): string | null => {
  if (val instanceof Date) {
    if (isNaN(val.getTime())) return null;
    return val.toISOString().split('T')[0];
  }
  if (typeof val === 'number') {
    try {
      const date = new Date(Math.round((val - 25569) * 86400 * 1000));
      if (!isNaN(date.getTime())) {
        const y = date.getUTCFullYear();
        const m = String(date.getUTCMonth() + 1).padStart(2, '0');
        const d = String(date.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
    } catch {
      return null;
    }
  }
  if (val) {
    const str = String(val).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      return str;
    }
    if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(str)) {
      const parts = str.split('.');
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
  }
  return null;
};

const parseTimeValue = (val: unknown): string | null => {
  if (val instanceof Date) {
    if (isNaN(val.getTime())) return null;
    const h = String(val.getHours()).padStart(2, '0');
    const m = String(val.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }
  if (typeof val === 'number') {
    const totalMinutes = Math.round(val * 24 * 60);
    const h = String(Math.floor(totalMinutes / 60) % 24).padStart(2, '0');
    const m = String(totalMinutes % 60).padStart(2, '0');
    return `${h}:${m}`;
  }
  if (val) {
    const str = String(val).trim();
    if (/^\d{1,2}:\d{2}$/.test(str)) {
      const [h, m] = str.split(':');
      return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
    }
    if (/^\d{1,2}:\d{2}:\d{2}$/.test(str)) {
      const [h, m] = str.split(':');
      return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
    }
  }
  return null;
};

export const importFromExcel = (
  fileData: ArrayBuffer | Uint8Array | string,
  existingProjects: Project[],
  existingLocations: Location[]
): {
  entries: TimeEntry[];
  projects: Project[];
  locations: Location[];
} => {
  const workbook = XLSX.read(fileData, { type: 'array', cellDates: true });
  
  const parsedBlocks: ParsedBlock[] = [];
  const projectsList = [...existingProjects];
  const locationsList = [...existingLocations];

  const getOrCreateProject = (name: string): string => {
    const cleanName = name.trim();
    if (!cleanName) return '';
    
    const normalize = (n: string) => n.toLowerCase().substring(0, 31).replace(/[\\/?*[\]]/g, '').trim();
    const normalizedTarget = normalize(cleanName);

    let proj = projectsList.find(p => p.name.toLowerCase() === cleanName.toLowerCase() || normalize(p.name) === normalizedTarget);
    if (!proj) {
      proj = {
        id: crypto.randomUUID(),
        name: cleanName
      };
      projectsList.push(proj);
    }
    return proj.id;
  };

  const getOrCreateLocation = (name: string): string => {
    const cleanName = name.trim();
    if (!cleanName) return '';
    let loc = locationsList.find(l => l.name.toLowerCase() === cleanName.toLowerCase());
    if (!loc) {
      loc = {
        id: crypto.randomUUID(),
        name: cleanName
      };
      locationsList.push(loc);
    }
    return loc.id;
  };

  for (const sheetName of workbook.SheetNames) {
    const worksheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { defval: '' });

    for (const row of rows) {
      const parsedDate = parseDate(row.Date);
      const parsedArrived = parseTimeValue(row['Time Arrived']);
      const parsedLeft = parseTimeValue(row['Time Left']);

      if (!parsedDate || !parsedArrived || !parsedLeft) {
        continue;
      }

      const rawBreak = row['Break Duration (hrs)'];
      const breakHrs = typeof rawBreak === 'number' ? rawBreak : parseFloat(String(rawBreak)) || 0;
      const breakMinutes = Math.round(breakHrs * 60);

      const description = String(row['What was worked on'] || '').trim();
      const locationName = String(row.Location || '').trim();

      parsedBlocks.push({
        date: parsedDate,
        projectName: sheetName,
        timeArrived: parsedArrived,
        timeLeft: parsedLeft,
        breakMinutes,
        description,
        locationName
      });
    }
  }

  const blocksByDate = new Map<string, ParsedBlock[]>();
  for (const block of parsedBlocks) {
    if (!blocksByDate.has(block.date)) {
      blocksByDate.set(block.date, []);
    }
    blocksByDate.get(block.date)!.push(block);
  }

  const entries: TimeEntry[] = [];
  const sortedDates = Array.from(blocksByDate.keys()).sort();

  for (const date of sortedDates) {
    const dayBlocks = blocksByDate.get(date)!;
    if (dayBlocks.length === 0) continue;

    const hasLateBlock = dayBlocks.some(b => parseTime(b.timeArrived) >= 18 * 60);

    const getAbsoluteMins = (timeStr: string): number => {
      const mins = parseTime(timeStr);
      if (hasLateBlock && mins < 6 * 60) {
        return mins + 24 * 60;
      }
      return mins;
    };

    let minArrivedMins = Infinity;
    let maxLeftMins = -Infinity;
    let overallBreakMins = 0;
    
    const projectMinutesMap = new Map<string, { minutes: number; description: string }>();
    let resolvedLocationId = '';
    let mainProjectId = '';
    let maxBreakMinsForMain = -1;

    for (const block of dayBlocks) {
      const arrAbs = getAbsoluteMins(block.timeArrived);
      let leftAbs = getAbsoluteMins(block.timeLeft);
      if (leftAbs < arrAbs) {
        leftAbs += 24 * 60;
      }

      if (arrAbs < minArrivedMins) {
        minArrivedMins = arrAbs;
      }
      if (leftAbs > maxLeftMins) {
        maxLeftMins = leftAbs;
      }

      overallBreakMins += block.breakMinutes;

      const projectId = getOrCreateProject(block.projectName);

      if (!resolvedLocationId && block.locationName) {
        resolvedLocationId = getOrCreateLocation(block.locationName);
      }

      const elapsed = leftAbs - arrAbs;
      const workMins = Math.max(0, elapsed - block.breakMinutes);

      if (block.breakMinutes > maxBreakMinsForMain) {
        maxBreakMinsForMain = block.breakMinutes;
        mainProjectId = projectId;
      }

      const existing = projectMinutesMap.get(projectId);
      if (existing) {
        const descriptions = [existing.description, block.description].filter(Boolean);
        projectMinutesMap.set(projectId, {
          minutes: existing.minutes + workMins,
          description: descriptions.join('; ')
        });
      } else {
        projectMinutesMap.set(projectId, {
          minutes: workMins,
          description: block.description
        });
      }
    }

    if (!resolvedLocationId && locationsList.length > 0) {
      resolvedLocationId = locationsList[0].id;
    }

    const entryProjects: EntryProject[] = Array.from(projectMinutesMap.entries())
      .filter(([, data]) => data.minutes > 0)
      .map(([projectId, data]) => {
        const totalDayWorkMins = Math.max(1, (maxLeftMins - minArrivedMins) - overallBreakMins);
        const percentage = Math.round((data.minutes / totalDayWorkMins) * 100);
        return {
          projectId,
          minutes: data.minutes,
          percentage,
          description: data.description
        };
      });

    const totalDayWorkMins = Math.max(0, (maxLeftMins - minArrivedMins) - overallBreakMins);
    const sumAllocatedMins = entryProjects.reduce((sum, p) => sum + p.minutes, 0);
    if (sumAllocatedMins !== totalDayWorkMins && entryProjects.length > 0) {
      const mainProjIndex = entryProjects.findIndex(p => p.projectId === mainProjectId);
      const targetIndex = mainProjIndex !== -1 ? mainProjIndex : 0;
      entryProjects[targetIndex].minutes += (totalDayWorkMins - sumAllocatedMins);
      entryProjects[targetIndex].percentage = Math.round((entryProjects[targetIndex].minutes / Math.max(1, totalDayWorkMins)) * 100);
    }

    entries.push({
      id: crypto.randomUUID(),
      date,
      timeArrived: formatTime(minArrivedMins),
      timeLeft: formatTime(maxLeftMins),
      breakDurationMinutes: Math.round(overallBreakMins),
      locationId: resolvedLocationId,
      mainProjectId: mainProjectId || (entryProjects[0]?.projectId || ''),
      projects: entryProjects
    });
  }

  return {
    entries,
    projects: projectsList,
    locations: locationsList
  };
};
